(() => {
  if (window.AthosProductList) {
    return;
  }

  class AthosProductList extends HTMLElement {
    static INIT_DELAY = 10;
    static READY_TIMEOUT = 5000;

    constructor() {
      super();
      this.controller = null;
      this.controllerSettings = null;
      this.athosReadyInterval = null;
      this.athosReadyTimeout = null;
      this.abortController = new AbortController();
    }

    connectedCallback() {
      this.whenAthosReady().then((ready) => {
        if (!ready) {
          return;
        }

        return this.run();
      });
    }

    disconnectedCallback() {
      this.clearAthosReadyTimers();
      this.abortController.abort();
    }

    clearAthosReadyTimers() {
      clearInterval(this.athosReadyInterval);
      clearTimeout(this.athosReadyTimeout);
    }

    whenAthosReady() {
      return new Promise((resolve) => {
        if (typeof athos !== 'undefined') {
          resolve(true);
          return;
        }

        if (!window.athosVariables?.siteId) {
          resolve(false);
          return;
        }

        this.athosReadyInterval = setInterval(() => {
          if (typeof athos !== 'undefined') {
            this.clearAthosReadyTimers();
            resolve(true);
            return;
          }

          if (!window.athosVariables?.siteId) {
            this.clearAthosReadyTimers();
            resolve(false);
          }
        }, AthosProductList.INIT_DELAY);

        this.athosReadyTimeout = setTimeout(() => {
          this.clearAthosReadyTimers();
          resolve(false);
        }, AthosProductList.READY_TIMEOUT);
      });
    }

    get profileTag() {
      return this.dataset.tag || 'recommendations';
    }

    get fallbackProfileTag() {
      return this.dataset.tagFallback || '';
    }

    get displayLimit() {
      return Number(this.dataset.limit) || 20;
    }

    get cardStyle() {
      return this.dataset.cardStyle || 'standard';
    }

    get athosStore() {
      return this.dataset.athosStore || 'default';
    }

    get productIds() {
      if (Array.isArray(this.dataset.productIds)) {
        return this.dataset.productIds;
      }

      return JSON.parse(this.dataset.productIds || '[]');
    }

    buildConfig(tag) {
      return {
        id: 'personalized-recommendations',
        tag,
        limit: this.displayLimit,
        globals: {
          products: this.productIds
        }
      };
    }

    async run() {
      if (!this.profileTag) {
        return;
      }

      let results = await this.requestProfile(this.profileTag);
      let usedFallback = false;

      if (results.length === 0 && this.fallbackProfileTag && this.fallbackProfileTag !== this.profileTag) {
        results = await this.requestProfile(this.fallbackProfileTag);
        usedFallback = results.length > 0;
      }

      if (results.length === 0) {
        this.onEmpty();
        return;
      }

      const displayed = await this.renderProducts(results);

      if (displayed.length === 0) {
        this.onEmpty();
        return;
      }

      this.trackImpressions(displayed);
      this.onRendered(usedFallback);
    }

    async requestProfile(tag) {
      this.controllerSettings = this.buildConfig(tag);
      this.controller = athos.initController('recommendations', this.controllerSettings, this.athosStore);

      if (!this.controller) {
        return;
      }

      this.controller.init();

      await this.controller.search();

      return [...this.controller.store.results];
    }

    async renderProducts(data) {
      const productHandles = data.map((product) => this.getHandle(product));

      const productCards = await athos.fetchProducts(productHandles, this.cardStyle);

      const slots = this.pairCardsToResults(productCards, data).slice(0, this.displayLimit);

      const rendered = this.renderSlots(slots) || [];

      return rendered.map((slot) => slot.product);
    }

    renderSlots(slots) {
      const target = this.querySelector('[data-list-target]');

      if (!target || slots.length === 0) {
        return [];
      }

      const template = this.querySelector('template[data-card-template]');

      target.replaceChildren();

      slots.forEach(({ card, product }) => {
        if (template) {
          const wrapper = template.content.cloneNode(true);

          (wrapper.querySelector('[data-card-slot]') || wrapper.firstElementChild).appendChild(card);

          target.appendChild(wrapper);
        } else {
          target.appendChild(card);
        }

        this.bindClickThrough(card, product);
      });

      return slots;
    }

    bindClickThrough(card, product) {
      const productLink = card.querySelector('a');

      productLink?.addEventListener('click', (event) => {
        this.controller.track.product.clickThrough(event, product);
      }, { signal: this.abortController.signal });
    }

    trackImpressions(displayed) {
      const impressionData = displayed.map((product, index) => ({
        type: 'product',
        position: index + 1,
        uid: product.mappings?.core?.uid || product.id || '',
        sku: product.mappings?.core?.sku || ''
      }));

      this.controller.tracker.events.recommendations.impression({
        data: {
          tag: this.controller.store.profile.tag,
          responseId: displayed[0].responseId,
          results: impressionData,
          banners: []
        }
      });
    }

    getHandle(product) {
      const parts = product.mappings.core.url.split('/');

      return parts[parts.length - 1].split('?')[0];
    }

    pairCardsToResults(cards, data) {
      const slots = [];
      let resultIndex = 0;

      cards.forEach((card) => {
        const cardHandle = card.dataset.productHandle;

        while (cardHandle && resultIndex < data.length && this.getHandle(data[resultIndex]) !== cardHandle) {
          resultIndex++;
        }

        if (resultIndex < data.length) {
          slots.push({ card, product: data[resultIndex] });

          resultIndex++;
        }
      });

      return slots;
    }

    onRendered() {
      this.classList.remove('hidden');
    }

    onEmpty() {}
  }

  window.AthosProductList = AthosProductList;
})();
